/**
 * Patient-wide documents index.
 *
 * Lists every document attached across every encounter for a patient,
 * with each row showing which encounter (date + chief complaint + status)
 * it belongs to. Click the encounter chip to open that encounter in a
 * new tab.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Stack,
    Avatar,
    Container,
    useTheme,
    Divider,
    IconButton,
    Tooltip,
    TextField,
    MenuItem,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import { listPatientDocuments, encounterDocumentDownloadUrl } from '../../../api/encounter';
import { apiGet } from '../../../api/client';

const NAVY = '#052F5D';

function formatDate(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function ageFromDob(dob) {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return age;
}

function initialsOf(p) {
    if (!p) return '·';
    const a = (p.fName || '')[0]; const b = (p.lName || '')[0];
    return `${a || ''}${b || ''}`.toUpperCase() || '·';
}

function humanSize(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function kindOf(mime) {
    if (!mime) return 'other';
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
    if (mime.includes('word')) return 'office';
    return 'other';
}

function iconFor(mime) {
    const k = kindOf(mime);
    if (k === 'image') return <ImageOutlinedIcon sx={{ color: NAVY }} />;
    if (k === 'pdf') return <PictureAsPdfOutlinedIcon sx={{ color: NAVY }} />;
    if (k === 'office') return <DescriptionOutlinedIcon sx={{ color: NAVY }} />;
    return <InsertDriveFileOutlinedIcon sx={{ color: NAVY }} />;
}

export default function PatientDocuments() {
    const { patientId, role: roleParam } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { userData } = useSelector((s) => s.auth);

    const [docs, setDocs] = useState([]);
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterKind, setFilterKind] = useState('all');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [docsRes, patRes] = await Promise.all([
                    listPatientDocuments(patientId),
                    apiGet(`api/get-patient-data/basicInformation/${patientId}`).catch(() => null),
                ]);
                if (cancelled) return;
                setDocs(docsRes.data || []);
                setPatient(patRes?.patientData || patRes?.data || patRes || null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [patientId]);

    const filtered = useMemo(() => {
        return docs.filter((d) => {
            if (filterKind !== 'all' && kindOf(d.mime) !== filterKind) return false;
            const t = d.uploadedAt ? new Date(d.uploadedAt).getTime() : 0;
            if (from && t < new Date(from).getTime()) return false;
            if (to && t > new Date(to).getTime() + 86400000) return false;
            return true;
        });
    }, [docs, filterKind, from, to]);

    const age = ageFromDob(patient?.dob);
    const role = userData?.role || roleParam || 'admin';

    return (
        <Container maxWidth={false} sx={{ maxWidth: 1320, px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 } }}>
            <Button
                onClick={() => navigate(`/${role}/patients/${patientId}/encounters`)}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500, mb: 1.5 }}
            >
                Back to encounters
            </Button>

            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 2px rgba(5,47,93,0.04)',
                }}
            >
                <Avatar sx={{ bgcolor: NAVY, color: '#fff', width: 52, height: 52, fontSize: 18, fontWeight: 600 }}>
                    {initialsOf(patient)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
                        Patient documents
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: NAVY, lineHeight: 1.2 }}>
                        {patient ? `${patient.fName || ''} ${patient.lName || ''}`.trim() || '—' : 'Loading…'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        MRN {patient?.mrn || '—'}
                        <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>·</Box>
                        {patient?.gender || '—'}
                        <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>·</Box>
                        {age !== null ? `${age} yrs` : '— yrs'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderOpenRoundedIcon sx={{ color: NAVY }} />
                    <Typography sx={{ color: NAVY, fontWeight: 600 }}>
                        {loading ? '…' : `${docs.length} document${docs.length === 1 ? '' : 's'}`}
                    </Typography>
                </Box>
            </Paper>

            {/* Filters */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 1.5,
                    mb: 2,
                    bgcolor: '#fff',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'center',
                }}
            >
                <TextField
                    select
                    size="small"
                    label="Type"
                    value={filterKind}
                    onChange={(e) => setFilterKind(e.target.value)}
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value="all">All types</MenuItem>
                    <MenuItem value="image">Images</MenuItem>
                    <MenuItem value="pdf">PDFs</MenuItem>
                    <MenuItem value="office">Word docs</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                </TextField>
                <TextField
                    type="date"
                    size="small"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                />
                <TextField
                    type="date"
                    size="small"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                />
                {(filterKind !== 'all' || from || to) && (
                    <Button
                        size="small"
                        onClick={() => { setFilterKind('all'); setFrom(''); setTo(''); }}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                        Clear filters
                    </Button>
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                    Showing {filtered.length} of {docs.length}
                </Typography>
            </Paper>

            {/* List */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: { xs: 1, md: 1.5 },
                    bgcolor: '#fff',
                }}
            >
                {loading ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <CircularProgress size={26} sx={{ color: NAVY }} />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box
                        sx={{
                            p: 5,
                            textAlign: 'center',
                            border: '1px dashed rgba(5,47,93,0.15)',
                            borderRadius: 2,
                            bgcolor: 'rgba(5,47,93,0.025)',
                        }}
                    >
                        <FolderOpenRoundedIcon sx={{ fontSize: 36, color: NAVY, opacity: 0.4 }} />
                        <Typography sx={{ mt: 1, color: NAVY, fontWeight: 600 }}>
                            {docs.length === 0 ? 'No documents attached to any encounter yet.' : 'No documents match the filter.'}
                        </Typography>
                    </Box>
                ) : (
                    <Stack divider={<Divider sx={{ borderColor: 'rgba(5,47,93,0.06)' }} />}>
                        {filtered.map((d) => {
                            const isFin = d.encounter?.status === 'finalized' || d.encounter?.status === 'signed';
                            const isSummary = d.kind === 'summary';
                            // The report as signed before an amendment replaced
                            // it. Listed so it stays retrievable, but visually
                            // demoted so it reads as history, not as the
                            // current summary.
                            const isSuperseded = d.kind === 'superseded_summary';
                            return (
                                <Box
                                    key={d.docId}
                                    sx={{
                                        p: { xs: 1.5, md: 2 },
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        bgcolor: isSummary ? 'rgba(46,125,50,0.03)' : 'transparent',
                                        transition: 'background-color 140ms ease',
                                        '&:hover': { bgcolor: isSummary ? 'rgba(46,125,50,0.06)' : 'rgba(5,47,93,0.025)' },
                                    }}
                                >
                                    {isSummary
                                        ? <DescriptionOutlinedIcon sx={{ color: 'success.dark' }} />
                                        : isSuperseded
                                            ? <DescriptionOutlinedIcon sx={{ color: 'text.disabled' }} />
                                            : iconFor(d.mime)}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: isSummary ? 'success.dark' : isSuperseded ? 'text.secondary' : NAVY,
                                                }}
                                                noWrap
                                            >
                                                {d.label || d.originalName || 'Document'}
                                            </Typography>
                                            {isSummary && (
                                                <Chip
                                                    size="small"
                                                    label="Visit summary"
                                                    sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: 'rgba(46,125,50,0.10)', color: 'success.dark', border: '1px solid rgba(46,125,50,0.25)' }}
                                                />
                                            )}
                                            {isSuperseded && (
                                                <Chip
                                                    size="small"
                                                    label="Superseded"
                                                    sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: 'rgba(0,0,0,0.05)', color: 'text.secondary', border: '1px solid rgba(0,0,0,0.15)' }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                            {d.originalName || ''}
                                            {d.sizeBytes ? ` · ${humanSize(d.sizeBytes)}` : ''}
                                            {d.uploadedAt ? ` · uploaded ${formatDateTime(d.uploadedAt)}` : ''}
                                            {d.uploadedBy?.name ? ` · by ${d.uploadedBy.name}` : ''}
                                        </Typography>
                                    </Box>
                                    <Box
                                        component="a"
                                        href={`/${role}/patients/${patientId}/encounters/${d.encounter._id}`}
                                        target="_blank"
                                        rel="noopener"
                                        sx={{
                                            textDecoration: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1.25,
                                            py: 0.5,
                                            borderRadius: 1,
                                            border: '1px solid rgba(5,47,93,0.15)',
                                            bgcolor: 'rgba(5,47,93,0.04)',
                                            color: NAVY,
                                            transition: 'background-color 140ms ease',
                                            '&:hover': { bgcolor: 'rgba(5,47,93,0.08)' },
                                            minWidth: 220,
                                            maxWidth: 320,
                                        }}
                                        title="Open encounter in new tab"
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                                Encounter · {formatDate(d.encounter?.visitDate)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }} noWrap>
                                                {d.encounter?.chiefComplaint || 'No chief complaint'}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            icon={isFin ? <LockOutlinedIcon sx={{ fontSize: 12 }} /> : <EditOutlinedIcon sx={{ fontSize: 12 }} />}
                                            label={d.encounter?.status || 'draft'}
                                            sx={{
                                                height: 20,
                                                fontSize: 10.5,
                                                fontWeight: 600,
                                                textTransform: 'capitalize',
                                                bgcolor: isFin ? 'rgba(46,125,50,0.10)' : 'rgba(5,47,93,0.08)',
                                                color: isFin ? 'success.dark' : NAVY,
                                                border: '1px solid',
                                                borderColor: isFin ? 'rgba(46,125,50,0.25)' : 'rgba(5,47,93,0.15)',
                                                '.MuiChip-icon': { color: 'inherit' },
                                            }}
                                        />
                                    </Box>
                                    <Tooltip title="Download" arrow>
                                        <IconButton
                                            component="a"
                                            href={encounterDocumentDownloadUrl(d.encounter._id, d.docId)}
                                            target="_blank"
                                            rel="noreferrer"
                                            size="small"
                                            sx={{ color: NAVY }}
                                        >
                                            <OpenInNewRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Paper>
        </Container>
    );
}
